/**
 * Dates as statements print them.
 *
 * Two jobs, from two directions:
 *
 *   * A PDF statement prints a transaction as `MAY 25` with no year at all;
 *     the year comes from the period header, and a December-January statement
 *     spans two of them. `parseStatementPeriod` and `inferTransactionDate` are
 *     ported unchanged from StableBooks (src/shared/statement-dates.ts) along
 *     with their tests — they are pure, leap-year aware, and already get the
 *     rollover right. They are here now because the PDF path will need them
 *     and they have nothing to do with PDFs.
 *
 *   * A CSV prints a whole date, but in whichever order its bank felt like:
 *     `2026-08-03`, `03/08/2026`, `08/03/2026`. `detectDateOrder` works out
 *     which — and says when it CANNOT, because 01/02/2026 is two different
 *     days and guessing silently would put a month of spending in the wrong
 *     month.
 *
 * Pure and Node-free: the browser parses the file, so this must run in both.
 */

const MONTHS = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC'
];

function monthIndex(abbr: string): number {
  return MONTHS.indexOf(abbr.toUpperCase().slice(0, 3));
}

function iso(year: number, monthIdx: number, day: number): string {
  const m = String(monthIdx + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function isDayValid(monthIdx: number, day: number, year: number): boolean {
  if (day < 1 || day > 31) return false;
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const maxDay = monthIdx === 1 && isLeapYear(year) ? 29 : daysInMonth[monthIdx];
  return day <= maxDay;
}

/* ==========================================================================
 * A printed statement's period
 * ======================================================================== */

/** `STATEMENT FROM MAY 26 TO JUN 25, 2026` → the period's two ISO dates. */
export function parseStatementPeriod(line: string): { from: string; to: string } | null {
  const m = /STATEMENT FROM ([A-Z]{3,}) (\d{1,2}) TO ([A-Z]{3,}) (\d{1,2}),\s*(\d{4})/i.exec(
    line ?? ''
  );
  if (!m) return null;
  const fromMonth = monthIndex(m[1]);
  const toMonth = monthIndex(m[3]);
  if (fromMonth < 0 || toMonth < 0) return null;

  const toYear = Number(m[5]);
  // The stated year belongs to the closing date. A period whose start month is
  // later than its end month began in the previous year.
  const fromYear = fromMonth > toMonth ? toYear - 1 : toYear;
  const fromDay = Number(m[2]);
  const toDay = Number(m[4]);

  if (!isDayValid(fromMonth, fromDay, fromYear) || !isDayValid(toMonth, toDay, toYear)) {
    return null;
  }

  return { from: iso(fromYear, fromMonth, fromDay), to: iso(toYear, toMonth, toDay) };
}

/**
 * `MAY 25` plus the statement period → a full ISO date.
 *
 * A transaction can be dated slightly BEFORE the period start (the period is
 * defined by posting dates), but never after the period end. So: take the
 * latest candidate year that still lands on or before the closing date, and
 * fall back to the earliest candidate if none does.
 */
export function inferTransactionDate(
  monthDay: string,
  periodFrom: string,
  periodTo: string
): string | null {
  const m = /^([A-Z]{3,})\s+(\d{1,2})$/i.exec((monthDay ?? '').trim());
  if (!m) return null;
  const monthIdx = monthIndex(m[1]);
  if (monthIdx < 0) return null;
  const day = Number(m[2]);

  const years = [...new Set([Number(periodFrom.slice(0, 4)), Number(periodTo.slice(0, 4))])].sort(
    (a, b) => a - b
  );
  const candidates = years
    .map((y) => iso(y, monthIdx, day))
    .filter((date) => isDayValid(monthIdx, day, Number(date.slice(0, 4))));

  if (candidates.length === 0) return null;

  const withinPeriod = candidates.filter((c) => c <= periodTo);
  return withinPeriod.length > 0 ? withinPeriod[withinPeriod.length - 1] : candidates[0];
}

/* ==========================================================================
 * A CSV's date column
 * ======================================================================== */

/** Which number comes first in a numeric date cell. */
export type DateOrder = 'ymd' | 'dmy' | 'mdy';

export interface DateOrderGuess {
  order: DateOrder;
  /**
   * True when the sample could not settle it — every cell reads as a valid
   * date either way round. The UI must SHOW this and let the owner choose;
   * 01/02/2026 is two different days and picking one quietly is how a month
   * of spending lands in the wrong month.
   */
  ambiguous: boolean;
  /** What was decided and on what evidence, for the mapping panel to print. */
  reason: string;
}

const ISO_CELL = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/;
const NUMERIC_CELL = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/;
/** `3 Aug 2026`, `03-AUG-2026`. */
const DAY_NAME_CELL = /^(\d{1,2})[-\s/]([A-Za-z]{3,})[-\s/.,]+(\d{4})$/;
/** `Aug 3, 2026`, `AUG 03 2026`. */
const NAME_DAY_CELL = /^([A-Za-z]{3,})[-\s/]+(\d{1,2})[-\s/.,]+(\d{4})$/;

function fourDigitYear(raw: string): number {
  const n = Number(raw);
  // A two-digit year on a bank export is this century; these statements do not
  // reach back to 1998.
  return raw.length === 2 ? 2000 + n : n;
}

/**
 * Which way round a column of numeric dates reads.
 *
 * A cell whose first number is over 12 can only be a day; one whose second
 * number is over 12 can only be a month-then-day. One such cell anywhere in
 * the column settles the whole column. When both appear the column contradicts
 * itself, which is worth saying out loud rather than averaging away.
 */
export function detectDateOrder(cells: readonly string[]): DateOrderGuess {
  const numeric: RegExpExecArray[] = [];
  let isoCount = 0;
  let namedCount = 0;
  let looked = 0;

  for (const raw of cells) {
    const cell = (raw ?? '').trim();
    if (cell === '') continue;
    looked++;
    if (ISO_CELL.test(cell)) {
      isoCount++;
      continue;
    }
    if (DAY_NAME_CELL.test(cell) || NAME_DAY_CELL.test(cell)) {
      namedCount++;
      continue;
    }
    const m = NUMERIC_CELL.exec(cell);
    if (m) numeric.push(m);
  }

  if (looked === 0) {
    return { order: 'ymd', ambiguous: true, reason: 'No dates to read.' };
  }
  if (isoCount > 0 && numeric.length === 0) {
    return { order: 'ymd', ambiguous: false, reason: 'Dates are written year first (2026-08-03).' };
  }
  if (namedCount > 0 && numeric.length === 0 && isoCount === 0) {
    // The month is spelled out, so nothing is ambiguous whichever order the
    // reader is set to.
    return { order: 'ymd', ambiguous: false, reason: 'Dates name their month (3 Aug 2026).' };
  }

  let dayFirst = 0;
  let monthFirst = 0;
  for (const m of numeric) {
    const a = Number(m[1]);
    const b = Number(m[2]);
    if (a > 12) dayFirst++;
    if (b > 12) monthFirst++;
  }

  if (dayFirst > 0 && monthFirst > 0) {
    return {
      order: 'dmy',
      ambiguous: true,
      reason: `The column contradicts itself — ${dayFirst} cell(s) can only be day first and ${monthFirst} can only be month first.`
    };
  }
  if (dayFirst > 0) {
    return {
      order: 'dmy',
      ambiguous: false,
      reason: `Day first: ${dayFirst} cell(s) have a first number over 12.`
    };
  }
  if (monthFirst > 0) {
    return {
      order: 'mdy',
      ambiguous: false,
      reason: `Month first: ${monthFirst} cell(s) have a second number over 12.`
    };
  }
  return {
    order: 'mdy',
    ambiguous: true,
    reason: 'Every date reads either way round (01/02/2026). Check which the bank meant.'
  };
}

/**
 * One date cell as an ISO date, or null when it is not a date at all.
 *
 * `order` only decides between the two numeric readings. A cell that names its
 * month, or writes the year first, says so itself and ignores the setting —
 * there is nothing left to choose.
 */
export function parseDateCell(text: string, order: DateOrder): string | null {
  const cell = (text ?? '').trim();
  if (cell === '') return null;

  const isoMatch = ISO_CELL.exec(cell);
  if (isoMatch) {
    return buildDate(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
  }

  const dayName = DAY_NAME_CELL.exec(cell);
  if (dayName) {
    return buildDate(Number(dayName[3]), monthIndex(dayName[2]), Number(dayName[1]));
  }

  const nameDay = NAME_DAY_CELL.exec(cell);
  if (nameDay) {
    return buildDate(Number(nameDay[3]), monthIndex(nameDay[1]), Number(nameDay[2]));
  }

  const numeric = NUMERIC_CELL.exec(cell);
  if (numeric) {
    const year = fourDigitYear(numeric[3]);
    const a = Number(numeric[1]);
    const b = Number(numeric[2]);
    // 'ymd' cannot apply to a two-number-then-year cell, so it reads as the
    // day-first form rather than refusing the row outright.
    return order === 'mdy' ? buildDate(year, a - 1, b) : buildDate(year, b - 1, a);
  }

  return null;
}

function buildDate(year: number, monthIdx: number, day: number): string | null {
  if (monthIdx < 0 || monthIdx > 11) return null;
  if (!Number.isFinite(year) || year < 1900 || year > 2999) return null;
  if (!isDayValid(monthIdx, day, year)) return null;
  return iso(year, monthIdx, day);
}
